/**
 * Hard timeout for backend requests. When the backend (or an upstream
 * dependency such as the Clerk Backend-API auth flow behind /loader/) stalls,
 * a plain `fetch` never settles, so TanStack Query stays `isPending` forever.
 * Aborting after this window lets the query transition to the error state so
 * the UI can surface the failure instead of hanging indefinitely.
 *
 * The backend now hard-caps its outbound Clerk calls at ~4s (ClerkService
 * withTimeout), so a legitimate signed-in /loader/ request should settle well
 * within a few seconds. Measured in-browser: successful /loader/ requests
 * ~0.7s, Clerk token round-trips 0.36–1.7s. 12s is ~3x headroom over that
 * worst legitimate case (slow Clerk + handler work) so legitimately slow
 * responses are NOT aborted, while a true hang still ends in bounded time.
 */
export const FETCH_BACKEND_TIMEOUT_MS = 25_000;

export function fetchBackend(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_BACKEND_TIMEOUT_MS);

  // Merge with a caller-provided signal so both can cancel.
  const callerSignal = init?.signal;
  if (callerSignal?.aborted) {
    controller.abort();
  } else if (callerSignal) {
    callerSignal.addEventListener('abort', () => controller.abort(), {
      once: true,
    });
  }

  const run = (input: RequestInfo | URL): Promise<Response> => {
    return fetch(input, {
      ...init,
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  };

  if (typeof input === 'object' && 'toString' in input) {
    return run(input);
  }

  if (typeof input === 'string') {
    // If it's a relative URL starting with /, use it directly
    // Vite proxy will handle forwarding to backend
    if (input.startsWith('/')) {
      return run(input);
    }

    // If it's a relative URL without leading slash, add it
    return run(`/${input}`);
  }

  return run(input);
}
